import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Shield, ShieldCheck, ArrowLeft, Copy, Anchor, KeyRound } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function MFASetup() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("init"); // init | scan | verify | done
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    if (user && user.mfa_enabled) {
      // Already enrolled — bounce back
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  const begin = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setData(data);
      setStep("scan");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/auth/mfa/verify", { code });
      setBackupCodes(r.data.backup_codes || []);
      setStep("done");
      await refresh();
      toast.success("MFA enrolled. The hull is secure.");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = (txt) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(txt);
      } else {
        const ta = document.createElement("textarea");
        ta.value = txt;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied.");
    } catch (_) {
      toast.error("Copy unavailable in this context.");
    }
  };

  return (
    <div className="min-h-screen relative">
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/4f56bc46-9ed9-46f3-bea8-9d960ca9759f/images/6206429cab63f493097092238009eeae506799f72aeb3c945b5532fbcf13939d.png')",
          backgroundSize: "cover",
          opacity: 0.4,
        }}
      />
      <div className="absolute inset-0 -z-10 bg-[#040404]/90" />

      {/* Header */}
      <header className="glass-panel-strong border-x-0 border-t-0">
        <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center border border-[#D8AB4E]/40 angled-corner">
              <Anchor size={16} className="text-[#D8AB4E]" />
            </div>
            <div className="leading-tight">
              <div className="label-mono text-[#D8AB4E]">SECURITY CONFIGURATION</div>
              <div className="font-display text-base">Multi-Factor Authentication</div>
            </div>
          </div>
          <button
            data-testid="mfa-setup-back"
            onClick={() => navigate("/")}
            className="btn-ghost-gold text-[10px] px-3 py-2 inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> BRIDGE
          </button>
        </div>
      </header>

      <main className="px-6 lg:px-10 py-12 max-w-3xl mx-auto" data-testid="mfa-setup-page">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="label-mono text-[#D8AB4E]">SECURITY · MFA ENROLMENT</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Bind Your Authenticator</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Six-digit codes from any TOTP authenticator (Google Authenticator, 1Password, Authy, Microsoft Authenticator).
            Backup recovery codes will be issued the moment your first code is verified.
          </p>
        </motion.div>

        {/* Step indicator */}
        <div className="flex items-center gap-3 mt-8 mb-8" data-testid="mfa-setup-steps">
          {["scan", "verify", "done"].map((s, i) => {
            const reached = ["scan", "verify", "done"].indexOf(step === "init" ? "scan" : step) >= i;
            return (
              <div key={s} className="flex items-center gap-3">
                <div
                  className={`h-7 w-7 grid place-items-center border font-mono-data text-xs ${
                    reached ? "border-[#D8AB4E] bg-[#D8AB4E] text-[#040404]" : "border-zinc-700 text-zinc-600"
                  }`}
                >
                  {i + 1}
                </div>
                <span className={`label-mono ${reached ? "text-[#D8AB4E]" : "text-zinc-600"}`}>
                  {s.toUpperCase()}
                </span>
                {i < 2 && <span className="hairline-x w-12" />}
              </div>
            );
          })}
        </div>

        {/* STEP: init */}
        {step === "init" && (
          <div className="glass-panel p-8 text-center" data-testid="mfa-init-card">
            <Shield size={44} className="text-[#D8AB4E] mx-auto" />
            <div className="font-display text-2xl mt-4">Begin Enrolment</div>
            <p className="text-zinc-400 max-w-md mx-auto mt-3">
              We'll generate a private TOTP secret and a QR code. Keep your authenticator app open.
            </p>
            {error && <div className="mt-4 text-xs font-mono-data text-[#E55A45]">{error}</div>}
            <button
              data-testid="mfa-begin-button"
              onClick={begin}
              disabled={busy}
              className="btn-gold mt-6 px-8 py-3 text-[11px] disabled:opacity-50"
            >
              {busy ? "GENERATING…" : "GENERATE CIPHER"}
            </button>
          </div>
        )}

        {/* STEP: scan */}
        {step === "scan" && data && (
          <div className="glass-panel p-8" data-testid="mfa-scan-card">
            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div>
                <div className="label-mono text-[#D8AB4E]">STEP 01</div>
                <h3 className="font-display text-2xl mt-1">Scan with Authenticator</h3>
                <div className="mt-5 inline-block p-4 bg-white" data-testid="mfa-qr">
                  <QRCodeSVG value={data.otpauth_uri} size={200} bgColor="#FFFFFF" fgColor="#040404" level="M" />
                </div>
                <div className="mt-5 space-y-2">
                  <div className="label-mono text-zinc-500">CAN'T SCAN? ENTER MANUALLY</div>
                  <div className="flex items-center gap-2 p-3 border border-[#D8AB4E]/25 bg-[#0A0A0A]">
                    <code className="font-mono-data text-xs text-[#D8AB4E] flex-1 break-all" data-testid="mfa-secret">
                      {data.secret}
                    </code>
                    <button
                      data-testid="mfa-copy-secret"
                      onClick={() => copy(data.secret)}
                      className="h-8 w-8 grid place-items-center border border-[#D8AB4E]/30 text-[#D8AB4E]"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
              </div>
              <div>
                <div className="label-mono text-[#D8AB4E]">STEP 02</div>
                <h3 className="font-display text-2xl mt-1">Confirm Cipher</h3>
                <p className="text-zinc-400 text-sm mt-3">
                  Enter the rolling six-digit code shown in your authenticator to bind this device.
                </p>
                <form onSubmit={verify} className="mt-6 space-y-4">
                  <input
                    data-testid="mfa-code-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000 000"
                    className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-4 py-4 text-center text-2xl tracking-[0.45em] font-mono-data text-[#D8AB4E] focus:outline-none focus:border-[#D8AB4E]"
                  />
                  {error && (
                    <div data-testid="mfa-verify-error" className="text-xs font-mono-data text-[#E55A45] border-l-2 border-[#C1432E] pl-3">
                      {error}
                    </div>
                  )}
                  <button
                    data-testid="mfa-verify-button"
                    type="submit"
                    disabled={busy || code.length !== 6}
                    className="btn-gold w-full py-3 text-[11px] disabled:opacity-50"
                  >
                    {busy ? "VERIFYING…" : "VERIFY & SECURE THE HULL"}
                  </button>
                </form>
                <div className="mt-6 flex items-center gap-2 text-[10px] font-mono-data tracking-[0.2em] text-zinc-500">
                  <KeyRound size={12} className="text-[#D8AB4E]" />
                  ISSUER · EIGOO SOVEREIGN BATTLESHIP
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP: done */}
        {step === "done" && (
          <div className="glass-panel-strong p-8" data-testid="mfa-done-card">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 grid place-items-center border border-[#10B981] bg-[#10B981]/10">
                <ShieldCheck size={20} className="text-[#10B981]" />
              </div>
              <div>
                <div className="label-mono text-[#10B981]">HULL SECURE</div>
                <h3 className="font-display text-2xl mt-1">MFA Enrolled</h3>
              </div>
            </div>
            <div className="gold-divider my-6" />
            <p className="text-zinc-300">
              Eight backup recovery codes have been issued. <span className="text-[#E55A45]">Save them now</span> — they will not be shown again.
              Each code may be used <span className="text-[#D8AB4E]">once</span> if you lose access to your authenticator.
            </p>
            <div
              className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
              data-testid="mfa-backup-codes"
            >
              {backupCodes.map((c, i) => (
                <div
                  key={i}
                  className="font-mono-data text-sm text-[#D8AB4E] border border-[#D8AB4E]/25 px-3 py-2.5 text-center select-all"
                >
                  {c}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                data-testid="mfa-copy-codes"
                onClick={() => copy(backupCodes.join("\n"))}
                className="btn-ghost-gold px-4 py-2.5 text-[10px] inline-flex items-center gap-2"
              >
                <Copy size={12} /> COPY ALL CODES
              </button>
              <button
                data-testid="mfa-finish-button"
                onClick={() => navigate("/")}
                className="btn-gold px-6 py-2.5 text-[10px]"
              >
                ENTER THE BRIDGE
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
